"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useActionState } from "react";
import { resetPasswordAndSendMail } from "@/features/firebase/auth/resetPasswordAndSendMail";
import { FormAction } from "@/features/common/type/commonType";
import { Loader2, ArrowLeft } from "lucide-react";
import { useState } from "react";

// パスワードリセットフォームの型定義
type ForgotPasswordFormState = {
  email: string;
};

export function ForgotPassword() {
  // フォーム送信後のメッセージ表示用の状態
  const [isSuccess, setIsSuccess] = useState(false);

  const handleResetPassword: FormAction<
    null | { state: ForgotPasswordFormState; error?: string }
  > = async (prevState, formData) => {
    const email = formData.get("email") as string;

    // 成功フラグをリセット
    setIsSuccess(false);

    // パスワードリセットメールを送信
    const result = await resetPasswordAndSendMail(email);

    if (result.success) {
      // 成功した場合
      setIsSuccess(true);
    }

    // フォームの状態とエラーを返す
    return {
      state: {
        email,
      },
      error: result.error,
    };
  };

  const [formState, formAction, isPending] = useActionState<
    null | { state: ForgotPasswordFormState; error?: string },
    FormData
  >(handleResetPassword, null);

  return (
    <div className="surface-panel flex flex-col items-center p-8 shadow-md">
      <div className="w-full space-y-8">
        <div className="flex flex-col items-center">
          <Image src="/byAgentLogo.svg" alt="byAgent" width={200} height={49} className="mb-8" />
          <h1 className="text-2xl font-bold">パスワードをお忘れの方</h1>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            登録したメールアドレスを入力すると、パスワード再設定用のリンクをメールでお送りします。
          </p>
        </div>

        {!isSuccess ? (
          <form action={formAction} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                name="email"
                defaultValue={formState?.state?.email}
                placeholder="メールアドレスを入力してください"
                autoComplete="email"
                required
              />
            </div>

            <div className="py-4">
              <Button type="submit" className="w-full" disabled={isPending}>
                {isPending ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    送信中...
                  </span>
                ) : (
                  "パスワードリセットメールを送信"
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="py-6 text-center">
            <div className="rounded-full bg-green-100 p-3 w-16 h-16 mx-auto flex items-center justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">メールを送信しました</h2>
            <p className="text-muted-foreground mb-6">
              {formState?.state?.email} が登録されている場合は、パスワード再設定用のリンクを送信しました。
              メール内のリンクからパスワードの再設定を行ってください。
            </p>
            <Button type="button" variant="outline" onClick={() => setIsSuccess(false)}>
              別のメールアドレスで試す
            </Button>
          </div>
        )}

        {formState?.error && !isSuccess && (
          <p className="mt-4 text-center text-sm text-destructive">{formState.error}</p>
        )}

        <div className="pt-4 text-center">
          <Link
            href="/login"
            className="inline-flex items-center text-sm text-link transition-colors duration-fast hover:text-link-hover"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            ログイン画面に戻る
          </Link>
        </div>
      </div>
    </div>
  );
}
